import React, { useEffect } from 'react';
import {
  AlertTriangle,
  FileWarning,
  Loader2,
  PieChart
} from 'lucide-react';
import useDashboardStore from '../../store/useDashboardStore';
import FinancialSummaryCards from '../../components/dashboard/FinancialSummaryCards';

const PIE_COLORS = ['#2563eb', '#059669', '#f97316', '#64748b'];
const BAR_COLOR = '#dc2626';

const formatCount = (value) => new Intl.NumberFormat('tr-TR').format(Number(value || 0));

const StatCard = ({ title, value, icon, tone }) => {
  const IconComponent = icon;
  const tones = {
    blue: 'bg-blue-50 text-blue-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    rose: 'bg-rose-50 text-rose-600',
    amber: 'bg-amber-50 text-amber-600'
  };

  return (
    <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</p>
          <p className="mt-3 text-2xl font-black text-slate-900 tracking-tight">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${tones[tone] || tones.blue}`}>
          <IconComponent size={24} />
        </div>
      </div>
    </div>
  );
};

const DonutChart = ({ data = [] }) => {
  const total = data.reduce((sum, item) => sum + Number(item.count || 0), 0);
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const segments = data.reduce((acc, item, index) => {
    const value = Number(item.count || 0);
    const dash = total ? (value / total) * circumference : 0;
    const previousOffset = acc.nextOffset;

    return {
      nextOffset: previousOffset + dash,
      items: [
        ...acc.items,
        {
          item,
          index,
          value,
          dash,
          offset: previousOffset
        }
      ]
    };
  }, { nextOffset: 25, items: [] }).items;

  if (!total) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-400 text-sm font-bold">
        Veri bulunamadı
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-center">
      <div className="relative w-56 h-56 mx-auto">
        <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="14" />
          {segments.map(({ item, index, value, dash, offset }) => (
              <circle
                key={item.name}
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke={PIE_COLORS[index % PIE_COLORS.length]}
                strokeWidth="14"
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
                strokeLinecap="round"
              >
                <title>{`${item.name}: ${formatCount(value)}`}</title>
              </circle>
          ))}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black text-slate-900">{formatCount(total)}</span>
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Toplam</span>
        </div>
      </div>

      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={item.name} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
              />
              <span className="text-sm font-bold text-slate-600 truncate">{item.name}</span>
            </div>
            <span className="text-sm font-black text-slate-900">{formatCount(item.count)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const MissingDocsBarChart = ({ data = [] }) => {
  const max = Math.max(...data.map((item) => Number(item.count || 0)), 1);

  if (data.length === 0) {
    return (
      <div className="h-72 flex items-center justify-center text-slate-400 text-sm font-bold">
        Eksik evraklı dosya bulunmuyor.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((item) => {
        const width = `${(Number(item.count || 0) / max) * 100}%`;
        return (
          <div key={item.representativeId || item.representativeName} className="grid grid-cols-[180px_1fr_48px] items-center gap-4">
            <div className="text-sm font-black text-slate-700 truncate">{item.representativeName}</div>
            <div className="h-10 bg-slate-100 rounded-2xl overflow-hidden">
              <div
                className="h-full rounded-2xl flex items-center justify-end px-3 text-xs font-black text-white transition-all"
                style={{ width, backgroundColor: BAR_COLOR }}
                title={`${item.representativeName}: ${formatCount(item.count)}`}
              >
                {Number(item.count || 0) > 0 && formatCount(item.count)}
              </div>
            </div>
            <div className="text-right text-sm font-black text-slate-900">{formatCount(item.count)}</div>
          </div>
        );
      })}
    </div>
  );
};

const Dashboard = () => {
  const { dashboardData, loading, error, fetchDashboardStats } = useDashboardStore();

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <Loader2 size={40} className="animate-spin text-blue-600" />
        <p className="text-sm font-black text-slate-400 uppercase tracking-widest">Dashboard hazırlanıyor</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-100 text-rose-700 rounded-[2rem] p-6 flex items-start gap-3">
        <AlertTriangle size={22} className="shrink-0 mt-0.5" />
        <div>
          <p className="font-black">Dashboard yüklenemedi</p>
          <p className="text-sm font-bold mt-1">{error}</p>
        </div>
      </div>
    );
  }

  const financialSummary = dashboardData?.financialSummary || {};
  const openInvoices = dashboardData?.openInvoices || financialSummary.openInvoices || {};
  const cariMetrics = dashboardData?.cariMetrics || financialSummary.cariMetrics || {};
  const personnelBalances = dashboardData?.personnelBalances || financialSummary.personnelBalances || {};
  const missingDocsTotal = (dashboardData?.representativeMissingDocs || [])
    .reduce((sum, item) => sum + Number(item.count || 0), 0);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Yönetici Paneli</h1>
          <p className="text-sm font-bold text-slate-500 mt-1">
            Finans, operasyon ve evrak risklerinin anlık görünümü
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-5">
        <FinancialSummaryCards
          openInvoices={openInvoices}
          cariMetrics={cariMetrics}
          personnelBalances={personnelBalances}
        />
        <StatCard
          title="Eksik Evraklı Dosya Sayısı"
          value={formatCount(missingDocsTotal)}
          icon={FileWarning}
          tone="amber"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <PieChart size={20} className="text-blue-600" />
            <h2 className="text-lg font-black text-slate-800">Operasyon Türü Dağılımı</h2>
          </div>
          <DonutChart data={dashboardData?.operationTypeDistribution || []} />
        </div>

        <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <PieChart size={20} className="text-emerald-600" />
            <h2 className="text-lg font-black text-slate-800">Taşıma Modu Dağılımı</h2>
          </div>
          <DonutChart data={dashboardData?.transportModeDistribution || []} />
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-lg font-black text-slate-800">Müşteri Temsilcisi Bazlı Eksik Evrak Analizi</h2>
            <p className="text-xs font-bold text-slate-400 mt-1">Operasyonel risk ve personel takip grafiği</p>
          </div>
        </div>
        <MissingDocsBarChart data={dashboardData?.representativeMissingDocs || []} />
      </div>
    </div>
  );
};

export default Dashboard;
